import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, ChevronDown, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CommandPaletteTrigger } from "@/components/CommandPalette";
import NotificationPanel from "./NotificationPanel";
import { useOwnerNotifications } from "@/hooks/useOwnerNotifications";
import "./notifications.css";

/**
 * Owner Console top bar — global controls only.
 *
 * Left  : global search (opens the Command Palette — same backing component)
 * Right : environment chip · quick actions · notifications · profile
 *
 * Duplicate page/module navigation removed per shell-refinement brief; the
 * sidebar is the single source of navigation truth.
 */
export default function OwnerTopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotif,   setShowNotif]   = useState(false);
  const bellBtnRef = useRef(null);

  const notif = useOwnerNotifications({ pollMs: 60000 });

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).slice(0, 2).map(s => s[0]).join("").toUpperCase();

  // Env detection: production URLs contain the customer domain; anything else
  // is treated as preview. Purely visual — no backend logic touched.
  const isProd = typeof window !== "undefined"
    && /hellobyflow\.com$/i.test(window.location.hostname);

  return (
    <header className="hbf-topbar" data-testid="owner-topbar">
      <CommandPaletteTrigger>
        <button
          type="button"
          className="hbf-topbar__search"
          data-testid="owner-search-trigger"
          aria-label="Open command palette"
        >
          <Search size={15} strokeWidth={2} className="hbf-muted" />
          <span className="hbf-topbar__search-placeholder">Search or run a command…</span>
          <span className="hbf-topbar__kbd">⌘K</span>
        </button>
      </CommandPaletteTrigger>

      <div className="hbf-topbar__spacer" />

      <div className="hbf-topbar__actions">
        <span
          className={`hbf-topbar__env ${isProd ? "is-prod" : ""}`}
          data-testid="owner-env-chip"
          title={isProd ? "Production environment" : "Preview / staging"}
        >
          <span className="hbf-topbar__env-dot" aria-hidden />
          {isProd ? "PRODUCTION" : "PREVIEW"}
        </span>

        <button
          type="button"
          className="hbf-topbar__icon-btn"
          aria-label="Quick actions"
          data-testid="owner-quick-actions-btn"
          onClick={() => nav("/owner/launch")}
          title="Launch Wizard"
        >
          <Zap size={17} strokeWidth={2} />
        </button>

        <div style={{ position: "relative" }}>
          <button
            ref={bellBtnRef}
            type="button"
            className="hbf-topbar__icon-btn"
            aria-label={notif.unreadTotal > 0 ? `Notifications, ${notif.unreadTotal} unread` : "Notifications"}
            aria-haspopup="dialog"
            aria-expanded={showNotif}
            data-testid="owner-notifications-btn"
            onClick={() => setShowNotif(v => !v)}
            title="Notifications"
          >
            <Bell size={17} strokeWidth={2} />
            {notif.unreadTotal > 0 && (
              <span className="hbf-topbar__badge" aria-hidden data-testid="owner-notifications-badge">
                {notif.unreadTotal > 99 ? "99+" : notif.unreadTotal}
              </span>
            )}
          </button>
          <NotificationPanel
            open={showNotif}
            anchorRef={bellBtnRef}
            onClose={() => setShowNotif(false)}
            unreadTotal={notif.unreadTotal}
            unreadByCategory={notif.unreadByCategory}
            groups={notif.groups}
            categories={notif.categories}
            loading={notif.loading}
            error={notif.error}
            onRefresh={notif.refreshGroups}
            onMarkRead={notif.markRead}
            onMarkAllRead={notif.markAllRead}
            onDismiss={notif.dismiss}
          />
        </div>

        <div
          className="hbf-topbar__profile"
          data-testid="owner-profile-menu"
          onClick={() => setShowProfile(v => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setShowProfile(v => !v); }}
        >
          <div className="hbf-topbar__avatar" aria-hidden>{initials}</div>
          <ChevronDown size={14} strokeWidth={2} className="hbf-muted" />
          {showProfile && (
            <div
              style={{
                position: "absolute", top: 46, right: 24,
                background: "var(--hbf-surface-1)",
                border: "1px solid var(--hbf-border)",
                borderRadius: "var(--hbf-radius-md)",
                boxShadow: "var(--hbf-shadow-lg)",
                padding: 8, minWidth: 240, zIndex: 20,
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="owner-profile-dropdown"
            >
              <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid var(--hbf-divider)" }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{user?.name || "Owner"}</div>
                <div style={{ fontSize: 11.5, color: "var(--hbf-text-subtle)" }}>{user?.email}</div>
              </div>
              <button type="button"
                onClick={() => { setShowProfile(false); nav("/owner/settings"); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, fontSize: 13, color: "var(--hbf-text-muted)" }}
                data-testid="owner-profile-settings">Settings</button>
              <button type="button"
                onClick={() => { setShowProfile(false); nav("/owner/security"); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, fontSize: 13, color: "var(--hbf-text-muted)" }}
                data-testid="owner-profile-security">Security</button>
              <button type="button"
                onClick={async () => { setShowProfile(false); await logout(); nav("/login"); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 6, fontSize: 13, color: "var(--hbf-danger)" }}
                data-testid="owner-profile-signout">Sign out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
